/**
 * Unit 2.IMPL.A — TanStack cell-renderer components for DocumentList.
 *
 * Bead: insurescope-zkolv.1.2.3.1.
 *
 * Lives next to `documentListColumns.tsx`. The split keeps the columns file
 * purely declarative (ColumnDef arrays + sizes) while giving each cell-level
 * component its own home, which:
 *   - satisfies `react-refresh/only-export-components` (component-only file),
 *   - lets stories cover individual cells in isolation in a later bead,
 *   - keeps the columns file under ~300 LOC.
 *
 * The status-glyph composer (`InlineStatusIndicatorOrIcon`) lives in
 * `components/shared/` so the library and the home Recent rows render the
 * identical glyph; this file only owns the per-type actions dispatcher.
 *
 * Exports:
 *   - `ActionsForRow` — per-type actions menu dispatcher (used by the
 *     actions column when NOT in move mode).
 *   - `SharedFolderContentsProvider` / `ForeignSharedBadge` — the name
 *     cell's badge for a foreign shared row, left out inside a shared folder
 *     whose context already names the group.
 */
import { createContext, useContext, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import {
    type ILibraryDocumentListItem,
    type ILibraryFolder,
    type ILibraryListComparison,
    type ILibraryListScope,
    ScopeStatus,
} from "@/api";

import { isForeignSharedSubject } from "../utils/folderTreeUtils";

import { ComparisonActionsMenu } from "./ComparisonActionsMenu";
import { DocumentActionsMenu } from "./DocumentActionsMenu";
import { FolderActionsMenu } from "./FolderActionsMenu";
import { ScopeActionsMenu } from "./ScopeActionsMenu";
import type { ILibraryRow } from "./documentListColumns.types";

const SharedFolderContentsContext = createContext(false);

export function SharedFolderContentsProvider({
    value,
    children,
}: {
    value: boolean;
    children: ReactNode;
}) {
    return (
        <SharedFolderContentsContext.Provider value={value}>
            {children}
        </SharedFolderContentsContext.Provider>
    );
}

export function ForeignSharedBadge({
    isProjectedRoot,
    groupKnowledgeBase,
}: {
    isProjectedRoot: boolean;
    groupKnowledgeBase: string;
}) {
    const insideSharedFolder = useContext(SharedFolderContentsContext);
    if (insideSharedFolder && !isProjectedRoot) return null;
    return (
        <Badge variant="secondary" className="min-w-0 shrink">
            <span
                className="truncate"
                title={isProjectedRoot ? undefined : groupKnowledgeBase}
            >
                {isProjectedRoot ? "Read-only" : groupKnowledgeBase}
            </span>
        </Badge>
    );
}

export function ActionsForRow({
    row,
    onAction,
}: {
    row: ILibraryRow;
    onAction: () => void;
}) {
    const raw = row._raw;

    if (row.type === "folder") {
        const folderForMenu: ILibraryFolder = {
            id: raw.id,
            name: raw.name,
            is_knowledge_base: raw.is_knowledge_base ?? false,
            is_shared_root: raw.is_shared_root ?? false,
            is_shared: raw.is_shared ?? false,
            client_id: raw.client_id,
            client:
                raw.client_id && raw.client_name
                    ? { id: raw.client_id, name: raw.client_name }
                    : null,
            parent_id: raw.parent_id,
            created_at: raw.created_at,
            updated_at: raw.updated_at ?? raw.created_at,
            shared_by_organization_name: raw.shared_by_organization_name,
            shared_group_name: raw.shared_group_name,
        } as ILibraryFolder;
        return <FolderActionsMenu folder={folderForMenu} onAction={onAction} />;
    }

    if (row.type === "document") {
        const docForMenu: ILibraryDocumentListItem = {
            id: raw.id,
            document_name: raw.name,
            file_type: raw.file_type,
            library_folder_id: raw.library_folder_id,
            industry_id: null,
            carrier_id: null,
            client_id: raw.client_id,
            industry_name: raw.industry_name,
            carrier_name: raw.carrier_name,
            client_name: raw.client_name,
            organization_name: null,
            textract_status: raw.status,
            page_count: null,
            file_size_bytes: null,
            created_at: raw.created_at,
        };
        return (
            <DocumentActionsMenu
                document={docForMenu}
                onAction={onAction}
                readOnly={isForeignSharedSubject(raw)}
            />
        );
    }

    if (row.type === "scope") {
        const scopeForMenu: ILibraryListScope = {
            type: "scope",
            id: raw.id,
            document_title: raw.name,
            status: raw.status as ScopeStatus,
            client_name: raw.client_name,
            clause_checklist_id: raw.clause_checklist_id,
            clause_checklist_name: raw.clause_checklist_name,
            library_folder_id: raw.library_folder_id,
            created_at: raw.created_at,
        };
        return <ScopeActionsMenu scope={scopeForMenu} onAction={onAction} />;
    }

    if (row.type === "comparison") {
        const compForMenu: ILibraryListComparison = {
            type: "comparison",
            id: raw.id,
            name: raw.name,
            client_name: raw.client_name,
            clause_checklist_name: raw.clause_checklist_name,
            scope_count: 0,
            library_folder_id: raw.library_folder_id,
            created_at: raw.created_at,
        };
        return (
            <ComparisonActionsMenu
                comparison={compForMenu}
                onAction={onAction}
            />
        );
    }

    return null;
}
